import { VACATION_HOURS_PERCENT, } from './vacation-payments.js';
/**
 * Automatyczne domknięcie otwartej sesji pracy.
 *
 * Klinika pracuje pn-pt 10:00–20:00. Sesja otwarta po 21:30 (Europe/Warsaw)
 * to zawsze „pracownik zapomniał kliknąć koniec pracy" — system domyka ją
 * sam, wysyła push i prosi o podanie właściwej godziny wyjścia.
 */
export const AUTO_CLOSE_HOUR = 21;
export const AUTO_CLOSE_MINUTE = 30;
export const AUTO_CLOSE_LABEL = '21:30';
/**
 * Maksymalny wiek zdarzenia z kolejki offline, które backend jeszcze przyjmie.
 * Starsze = urządzenie leżało wyłączone tygodniami; wtedy korektę robi admin.
 */
export const MAX_OFFLINE_EVENT_AGE_DAYS = 14;
/**
 * Pełny dzień urlopu w rozliczeniu czasu pracy = 8h (× ułamek etatu).
 *
 * To wartość BAZOWA — ile godzin ma pełny dzień. Ile z tego zalicza konkretny
 * typ nieobecności, decyduje `VACATION_HOURS_PERCENT` (L4 = 80%).
 */
export const VACATION_DAY_HOURS = 8;
/**
 * Godziny zaliczone za jeden dzień nieobecności danego typu.
 *
 * Bez `type` zwraca pełny dzień (8h × etat) — tak liczą się urlopy płatne
 * w 100%. Z `type` nakłada procent z `VACATION_HOURS_PERCENT`: L4 przy pełnym
 * etacie → 6,4 h, przy 1/2 etatu → 3,2 h.
 *
 * Ta sama funkcja stoi za progresem pracownika (`monthly-progress.ts`) i za
 * rozliczeniem w raporcie admina (`admin/reports.ts`), więc karta pracownika
 * i lista księgowej nigdy się nie rozjadą.
 */
export function vacationDayHours(employmentFraction, type) {
    const pct = type ? VACATION_HOURS_PERCENT[type] / 100 : 1;
    return Math.round(VACATION_DAY_HOURS * employmentFraction * pct * 100) / 100;
}
/**
 * Które nieobecności zaliczają się do rozliczenia jako przepracowane godziny.
 *
 * Pochodna `VACATION_HOURS_PERCENT` — jedno źródło prawdy. BEZPLATNY jako
 * jedyny ma 0% i nie daje ani wynagrodzenia, ani zaliczenia normy. Reszta
 * (wypoczynkowy, na żądanie, opieka nad dzieckiem, L4) nie może działać na
 * niekorzyść pracownika przy ocenie „czy wyrobił etat" — L4 zalicza się,
 * tyle że w wymiarze 80% dnia.
 */
export const VACATION_COUNTS_AS_WORK_TIME = Object.fromEntries(Object.entries(VACATION_HOURS_PERCENT).map(([type, pct]) => [type, pct > 0]));
/**
 * Przelicza dni nieobecności per typ na godziny zaliczone do rozliczenia.
 *
 * JEDYNE miejsce, w którym `VACATION_HOURS_PERCENT` dotyka liczb. Wołane
 * i przez raport admina (`admin/reports.ts`), i przez progres pracownika
 * (`services/monthly-progress.ts`) — dzięki temu obie strony pokazują tę samą
 * liczbę godzin za ten sam miesiąc.
 *
 * Wynagrodzenie za nieobecność liczy się jako `hours × pełna stawka` —
 * procent jest już w godzinach, nie wolno go nakładać drugi raz.
 */
export function vacationHoursBreakdown(daysByType, employmentFraction) {
    const fullDayHours = vacationDayHours(employmentFraction);
    const byType = {};
    let countedDays = 0;
    let unpaidLeaveDays = 0;
    let hours = 0;
    for (const [key, percent] of Object.entries(VACATION_HOURS_PERCENT)) {
        const type = key;
        const days = daysByType[type] ?? 0;
        const typeHours = Math.round(days * vacationDayHours(employmentFraction, type) * 100) / 100;
        byType[type] = { days, percent, hours: typeHours };
        if (percent > 0) {
            countedDays += days;
            hours += typeHours;
        }
        else {
            unpaidLeaveDays += days;
        }
    }
    return {
        byType,
        fullDayHours,
        countedDays,
        unpaidLeaveDays,
        hours: Math.round(hours * 100) / 100,
    };
}
/**
 * Próg „koniec pracy z daleka od placówki".
 *
 * Pracownik, który kliknął „Koniec pracy" dalej niż tyle metrów od pinezki
 * kliniki, dostaje pytanie o godzinę, o której faktycznie wyszedł — bo
 * kliknięcie w tramwaju albo w domu zawyża jego czas pracy.
 *
 * Próg jest ŚWIADOMIE luźniejszy niż `geofenceRadiusM`: tamten flaguje sesję
 * adminowi (może być ciasny, fałszywy alarm nikogo nie kosztuje), ten zaczepia
 * pracownika przy każdym wyjściu (fałszywy alarm = irytacja i klikanie na
 * ślepo). Admin może go podnieść w ustawieniach.
 */
export const DEFAULT_END_PROMPT_RADIUS_M = 200;
export const MIN_END_PROMPT_RADIUS_M = 100;
export const MAX_END_PROMPT_RADIUS_M = 5000;
/**
 * Ile dni pytamy pracownika o godzinę wyjścia (auto-close i „koniec z daleka").
 * Później korektę robi już admin w panelu.
 */
export const END_PROMPT_WINDOW_DAYS = 14;
/**
 * Dystans po polsku: metry z zaokrągleniem do 10 m, kilometry z przecinkiem.
 * `450` → „450 m", `1520` → „1,5 km". Używane w modalu pracownika i w pushu
 * do admina, żeby obie strony widziały tę samą liczbę.
 */
export function formatDistancePl(meters) {
    if (!Number.isFinite(meters) || meters < 0)
        return '—';
    if (meters < 1000) {
        const rounded = meters < 100 ? Math.round(meters) : Math.round(meters / 10) * 10;
        return `${rounded} m`;
    }
    return `${(meters / 1000).toFixed(1).replace('.', ',')} km`;
}
//# sourceMappingURL=work-time.js.map